import { useEffect, useMemo, useRef } from 'react';
import { PrefillType } from '@weave/schema-gen-ts/dist/schemas/payments-platform/care-credit-integration/care_credit_integration.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import { CareCreditIntegrationCustom, CareCreditIntegrationV1 } from '@frontend/api-care-credit-integration';
import { InvoiceModel } from '@frontend/api-invoices';
import { PersonsV3 } from '@frontend/api-person';
import { CareCreditChipStatus, CareCreditPatientData } from '../types/care-credit.types';
import { getQuickOfferStatus } from '../widgets/care-credit-enrollment-multi-step/utils/quick-offer';
import { useCareCreditPurlQuery } from './use-care-credit-purl-query';

export type UseCareCreditPatientDataProps = {
  person: Person | undefined;
  locationId: string | undefined;
  merchantNumber: string | undefined;
  isDecoupled: boolean;
  userId: string;
  invoice?: InvoiceModel;
};
export type UseCareCreditPatientDataOptions = {
  enabled?: boolean;
  usePurlFallback?: boolean;
  prefillType?: PrefillType;
};

export const useCareCreditPatientData = (
  { person, locationId, merchantNumber, isDecoupled, userId, invoice }: UseCareCreditPatientDataProps,
  { enabled, usePurlFallback = true, prefillType }: UseCareCreditPatientDataOptions = {}
) => {
  const queryClient = useQueryClient();
  const personDataSignature = JSON.stringify(CareCreditIntegrationCustom.getPatientQueryFields(person));
  const previousPersonDataSignature = useRef<string>();

  useEffect(() => {
    if (
      previousPersonDataSignature.current &&
      previousPersonDataSignature.current !== personDataSignature &&
      person?.personId &&
      locationId &&
      merchantNumber
    ) {
      queryClient.invalidateQueries({
        queryKey: CareCreditIntegrationCustom.getCareCreditPatientDataQueryKey(person, locationId, merchantNumber),
        exact: true,
      });
    }

    previousPersonDataSignature.current = personDataSignature;
  }, [locationId, merchantNumber, person, personDataSignature, queryClient]);

  const {
    // even though this is called useCareCreditPatientDataQuery, internally it calls
    // the quickscreen offer endpoint. rather than renaming the entire hook name,
    // i'm just renaming the response data locally to make the control flow easier
    // to read within the context of this file.
    query: { isLoading, isRefetching, isFetching, isSuccess, error, data: quickScreenOfferResponseData },
  } = CareCreditIntegrationCustom.useCareCreditPatientDataQuery(
    {
      person,
      merchantNumber,
      locationId,
    },
    {
      enabled,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  // initial quick offer status gets the status from the quickscreen offer
  // request. this is important because there is a scenario where the
  // quickscreen endpoint may indicate that someone is a cardholder while
  // account lookup will indicate that their account is closed.
  const initialQuickOfferStatus = quickScreenOfferResponseData
    ? getQuickOfferStatus(quickScreenOfferResponseData)
    : undefined;

  // Use the same mapping as the contact panel: MobilePhone with fallback to OtherContactInfo (UNSPECIFIED)
  const convertedPerson = useMemo(() => PersonsV3.PersonHelpers.convertPersonV3ToPerson(person), [person]);
  const mobilePhone = convertedPerson.MobilePhone || convertedPerson.OtherContactInfo;

  // this var is reused in a few places, including to determine if the getQuickOfferStatus
  // helper function should take the accountLookup response into consideration when
  // computing `status`
  const isAccountLookupEnabled =
    enabled !== false && !!merchantNumber && !!mobilePhone && !!locationId && initialQuickOfferStatus === 'CARDHOLDER';

  const {
    data: accountLookupData,
    error: accountLookupError,
    isLoading: isAccountLookupLoading,
    isFetching: isAccountLookupFetching,
  } = CareCreditIntegrationV1.Queries.useAccountLookupQuery({
    request: { merchantNumber, phoneNumber: mobilePhone, locationId },
    options: {
      enabled: isAccountLookupEnabled,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });

  // since quickscreen and account lookup may return data that conflicts
  // with the quickscreen data, we need to wait for the account lookup
  // request to return its response before we can know whether or not
  // the patient is truly a cardholder.
  const isAccountNumberLoading = isAccountLookupLoading || isAccountLookupFetching;
  const isWaitingForAccountLookup =
    isAccountLookupEnabled && !accountLookupData && !accountLookupError && isAccountNumberLoading;
  const quickOfferStatus =
    quickScreenOfferResponseData && !isWaitingForAccountLookup
      ? getQuickOfferStatus(quickScreenOfferResponseData, isAccountLookupEnabled ? accountLookupData : undefined)
      : undefined;

  const { purl, isPurlLoading, purlError } = useCareCreditPurlQuery(
    {
      locationId,
      isDecoupled,
      userId,
      careCreditPurlTransaction: {
        // invoice number is different from the paymentRequestId
        // in that it is entered by the user in the case of a
        // decoupled flow and can be used by the user when
        // reconciling their PMS ledger against care credit
        invoiceNumber: invoice?.id || '',
        // the paymentRequestId is the weave identifier
        // for the payment request.
        paymentRequestId: invoice?.id || '',
        purchaseAmountInCents: invoice?.billedAmount || 0,
        serviceDate: invoice?.billedAt || '',
      },
      prefillType,
      person,
    },
    {
      enabled:
        enabled !== false &&
        usePurlFallback &&
        !error &&
        !!quickOfferStatus &&
        quickOfferStatus !== 'UNKNOWN' &&
        (prefillType !== PrefillType.PREFILL_TYPE_UNSPECIFIED ||
          (!isLoading && !isRefetching && !isFetching && !quickScreenOfferResponseData?.locationUrl)) &&
        !!person?.personId,
    }
  );

  const careCreditData = useMemo<CareCreditPatientData | undefined>(() => {
    if (!quickScreenOfferResponseData || !quickOfferStatus) {
      return undefined;
    }

    const status = quickOfferStatus;
    const accountNumber = accountLookupData?.data?.account?.accountInfo?.accountNumber;

    if (status === 'CARDHOLDER') {
      const accountLookupBalance = accountLookupData?.data?.account?.creditInfo?.availableCredit;
      const quickOfferBalance = quickScreenOfferResponseData.errorResponse?.accountInfo?.availableBalance;
      const availableCredit =
        accountLookupBalance !== undefined && accountLookupBalance !== null && accountLookupBalance !== ''
          ? accountLookupBalance
          : quickOfferBalance;

      return {
        personId: person?.personId || '',
        status,
        amount: Number(availableCredit) || 0,
        careCreditPersonId:
          quickScreenOfferResponseData.errorResponse?.accountInfo?.accountNumber || accountNumber || '',
        accountNumber,
        asOfDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        patientLink: purl?.purl || quickScreenOfferResponseData.locationUrl || '',
      };
    }
    return {
      personId: person?.personId || '',
      status,
      amount: quickScreenOfferResponseData.plccOfferInfo?.creditLine
        ? Number(quickScreenOfferResponseData.plccOfferInfo.creditLine)
        : 0,
      careCreditPersonId: '',
      accountNumber,
      asOfDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      patientLink: purl?.purl || quickScreenOfferResponseData.locationUrl || '',
    };
  }, [
    accountLookupData?.data?.account?.accountInfo?.accountNumber,
    accountLookupData?.data?.account?.creditInfo?.availableCredit,
    person?.personId,
    purl?.purl,
    quickScreenOfferResponseData,
    quickOfferStatus,
  ]);

  const careCreditChipStatus: CareCreditChipStatus | undefined =
    isLoading || isRefetching || isFetching || isWaitingForAccountLookup
      ? undefined
      : error || (quickScreenOfferResponseData && !quickOfferStatus)
      ? 'ERROR'
      : quickOfferStatus || 'UNKNOWN';

  return {
    isBusy: isLoading || isRefetching || isFetching || isPurlLoading || isAccountNumberLoading,
    isLoading: isLoading || isPurlLoading || isAccountNumberLoading,
    isSuccess,
    error,
    purlError,
    isAccountNumberLoading,
    errorResponse: quickScreenOfferResponseData?.errorResponse,
    careCreditData,
    careCreditChipStatus,
  };
};

export type UseCareCreditPatientData = ReturnType<typeof useCareCreditPatientData>;
